import React, { ReactElement, useEffect, useMemo, useState } from "react";
import {
  MantineReactTable,
  MRT_ColumnDef,
  useMantineReactTable,
} from "mantine-react-table";
import { useQuery } from "@tanstack/react-query";
import { QueryKey } from "../../models/enums";
import { IndexerService } from "../../services/indexer.service";
import {
  EpochData,
  NodeHistory,
  UptimeEpochData,
  UptimeInterval,
} from "../../models/NodeHistory";
import { formatNumber, getYearlyROI } from "../../utils/formatter";
import { Group, Text, Tooltip } from "@mantine/core";
import { IconAlertTriangle, IconHelpSquareRounded } from "@tabler/icons-react";

interface CompleteEpochData extends EpochData {
  uptime: number | null;
}

export default function NodeHistoryTable({
  nodeId,
}: {
  nodeId: string;
}): ReactElement {
  const [combinedEpochData, setCombinedEpochData] = useState<
    CompleteEpochData[]
  >([]);

  const noRewardsLabel = `Possible reasons:
  - didn't provide its FTSO address
  - not high enough uptime
  - not high enough FTSO performance`;

  const { data: uptimeByEpoch } = useQuery({
    queryKey: [QueryKey.uptimeEpoch],
    queryFn: (): Promise<UptimeEpochData | null> =>
      IndexerService.getUptimeData(nodeId!),
    enabled: !!nodeId,
  });

  const { data: epochData } = useQuery({
    queryKey: [QueryKey.epochData],
    queryFn: (): Promise<NodeHistory> => IndexerService.getNodeHistory(nodeId!),
    enabled: !!nodeId,
  });

  useEffect(() => {
    if (uptimeByEpoch && epochData) {
      const mappedData: CompleteEpochData[] = epochData.epochs.map(
        (epoch: EpochData) => {
          const uptime: number | null =
            uptimeByEpoch.intervals.find(
              (interval: UptimeInterval) => interval.epoch === epoch.epochNumber
            )?.uptimePercent ?? null;

          return {
            epochNumber: epoch.epochNumber,
            eligible: epoch.eligible,
            roi: epoch.roi,
            uptime: uptime,
          } as CompleteEpochData;
        }
      );
      setCombinedEpochData(mappedData);
    }
  }, [uptimeByEpoch, epochData]);

  const columns = useMemo<MRT_ColumnDef<CompleteEpochData>[]>(
    () => [
      {
        accessorKey: "epochNumber",
        header: "Epoch number",
        Header: () => (
          <Group>
            <Text fw={500}>Epoch number</Text>
          </Group>
        ),
        mantineTableHeadCellProps: {
          align: "left",
        },
        mantineTableBodyCellProps: {
          align: "left",
        },
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        accessorKey: "uptime",
        header: "Uptime",
        Header: () => (
          <Group>
            <Text fw={500}>Uptime</Text>
          </Group>
        ),
        mantineTableHeadCellProps: {
          align: "left",
        },
        mantineTableBodyCellProps: {
          align: "left",
        },
        enableSorting: true,
        enableColumnFilter: false,
        Cell: ({ cell }) => (
          <>
            {cell.getValue<number>()
              ? formatNumber(cell.getValue<number>() * 100, false, 1) + "%"
              : "No data"}
          </>
        ),
      },
      {
        accessorKey: "eligible",
        header: "Rewards",
        Header: () => (
          <Group>
            <Text fw={500}>Rewards</Text>
          </Group>
        ),
        mantineTableHeadCellProps: {
          align: "left",
        },
        mantineTableBodyCellProps: {
          align: "left",
        },
        size: 120,
        enableSorting: true,
        enableColumnFilter: false,
        Cell: ({ cell }) => (
          <Group gap={5}>
            {cell.getValue<number>() ? "Yes" : "No"}
            {!cell.getValue<number>() && (
              <>
                <Tooltip
                  label={noRewardsLabel}
                  multiline
                  w={300}
                  position="top"
                  events={{
                    hover: true,
                    focus: false,
                    touch: true,
                  }}
                  styles={{
                    tooltip: { whiteSpace: "pre-line" },
                  }}
                >
                  <IconAlertTriangle
                    size={16}
                    style={{
                      flexShrink: 0,
                      color: "var(--mid-dark)",
                    }}
                  />
                </Tooltip>
              </>
            )}
          </Group>
        ),
      },
      {
        accessorKey: "roi",
        header: "APY",
        Header: () => (
          <Group gap={5}>
            <Text fw={500}>APY</Text>
            <Tooltip
              label={
                "Estimated annual percentage return based on rewards in latest Epoch."
              }
              multiline
              w={220}
              position="top"
              events={{
                hover: true,
                focus: false,
                touch: true,
              }}
            >
              <IconHelpSquareRounded size={20} style={{ flexShrink: 0 }} />
            </Tooltip>
          </Group>
        ),
        size: 110,
        mantineTableHeadCellProps: {
          align: "right",
        },
        mantineTableBodyCellProps: {
          align: "right",
        },
        enableSorting: true,
        enableColumnFilter: false,
        Cell: ({ cell }) => (
          <>
            <>{formatNumber( getYearlyROI(cell.getValue<number>() * 100), false, 2) + "%"}</>
          </>
        ),
      },
    ],
    [combinedEpochData]
  );
  const table = useMantineReactTable({
    columns: combinedEpochData ? columns : [],
    data: combinedEpochData, //must be memoized or stable (useState, useMemo, defined outside of this component, etc.)
    mantineTableProps: {
      style: {
        tableLayout: "fixed",
        fontWeight: 500,
        fontSize: "var(--module-size-14)",
      },
    },
    mantineTableHeadCellProps: {
      style: {
        fontWeight: 500,
        fontSize: 15,
      },
    },
    state: {
      isLoading: !combinedEpochData,
      // pagination,
      // sorting,
    },
    mantinePaperProps: {
      withBorder: false,
      shadow: "none",
    },
    enableColumnActions: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    enableFilters: false,
    initialState: {
      density: "xs",
      sorting: [
        {
          id: "epochNumber",
          desc: true,
        },
      ],
    },
    // rowCount: delegationsList?.count,
    // onPaginationChange: setPagination, //hoist pagination state to your state when it changes internally
    // onSortingChange: setSorting,
    mantinePaginationProps: {
      showRowsPerPage: false,
      withEdges: false,
    },
  });

  return (
    <div style={{ height: combinedEpochData.length >= 10 ? 580 : "unset" }}>
      <MantineReactTable table={table} />
    </div>
  );
}
